import { caseTaskSchema } from "@/components/Forms/schema/addCaseTasksSchema.ts";
import {
    caseFormSchemaInput,
    clientSchema,
} from "@/components/Forms/schema/caseFormSchemaInput.ts";
import { personSchema } from "@/components/Forms/schema/personSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceSelector } from "jsonapi-zod-query";

const resourceSelector = createResourceSelector({
    type: "case",
    attributesSchema: caseFormSchemaInput,
    relationships: {
        idClient: {
            relationshipType: "one_nullable",
            include: {
                type: "client",
                attributesSchema: clientSchema,
            },
        },
        idPeople: {
            relationshipType: "one_nullable",
            include: {
                type: "person",
                attributesSchema: personSchema,
            },
            required: false,
        },
        caseTasks: {
            relationshipType: "many",
            include: {
                type: "caseTask",
                attributesSchema: caseTaskSchema,
            },
            required: true,
        },
    },
});

export const caseSelector = createDataSelector(resourceSelector);
export type CaseData = ReturnType<typeof caseSelector>;

export type CaseDocument = ReturnType<typeof resourceSelector>;

export const useFetchCase = (caseId?: string): UseQueryResult<CaseDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["case", caseId],
        enabled: !!caseId,
        queryFn: async ({ signal }) => {
            const url = apiUrl(`/case/by-id/${caseId}`);

            const response = await fetch(url, {
                signal,
                headers: {
                    accept: "application/vnd.api+json",
                },
            });

            return await response.json();
        },
        staleTime: 60_000, // 1 minute - case data doesn't change frequently
        gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache longer
        refetchOnWindowFocus: false, // Don't refetch when user returns to window
        refetchOnMount: true, // Refetch when component mounts if data is stale
        select: resourceSelector,
    });
};
